<template>

    <div class="page">

        <!-- KtDatatable Implementation -->
        <kt-datatable
            :model="expenseModel"
            :data="rows"
            :filter="datatableFilter"
            :meta="{ tableClass: 'table-bordered' }"
            :update-function="handleDatatableUpdate"
            :hide-search="false"
            storage-key="expense-list"
        >
            <template #header_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectAll"
                        @change="toggleSelectAll"
                        @click.stop
                        title="Select all on this page"
                    />
                </div>
            </template>
            
            <template #view_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectedRows.includes(scope.row.Id)"
                        @change="toggleRowSelection(scope.row.Id)"
                        @click.stop
                    />
                </div>
            </template>
            
            <template #filters="scope">
                <div class="col" v-if="!props.hideFilters">
                    <div class="row">
                        <div class="col-auto">
                            <client-multi-check 
                                label="Clients"
                                :model-value="currentFilter.client"
                                @update:model-value="(value: any) => setFilter({client: value})"
                            ></client-multi-check>
                        </div>
                        <div class="col-auto">
                            <tech-multi-check 
                                label="Technicians"
                                :model-value="currentFilter.technician"
                                @update:model-value="(value: any) => setFilter({technician: value})"
                            ></tech-multi-check>
                        </div>
                        <div class="col-auto">
                            <kt-date-range-select
                                label="Date Range"
                                v-model="dateRange"
                                @change="handleDateRangeChange"
                            ></kt-date-range-select>
                            <!-- 
                            <kt-date-range-picker 
                                label=""
                                placeholder="Date Range..."
                                v-model="dateRange"
                                input-class="form-control-sm"
                            ></kt-date-range-picker>
                            -->
                        </div>
                    </div>
                </div>
            </template>

            <template #view_actions="scope">
                <kt-dropmenu v-model:visible="actionDropdown['row_' + scope.row.Id]">
                    <template #button>
                        <button
                            class="btn -btn-outline-primary btn-sm"
                            @click="actionDropdown['row_' + scope.row.Id] = !actionDropdown['row_' + scope.row.Id]"
                        >
                            <span><i class="fas fa-ellipsis-v"></i></span>
                        </button>
                    </template>
                    <template #dropmenu>
                        <ul class="dropdown-menu show">
                            <li>
                                <label class="dropdown-item">
                                    <button class="btn btn-sm btn-outline-primary form-control" @click="editRecord(scope.row.Id)">
                                        <i class="fa fa-edit"></i> Edit
                                    </button>
                                </label>
                            </li>
                            <li>
                                <label class="dropdown-item">
                                    <button class="btn btn-sm btn-outline-danger form-control" @click="deleteRecord(scope.row.Id)">
                                        <i class="fa fa-trash"></i> Remove
                                    </button>
                                </label>
                            </li>
                        </ul>
                    </template>
                </kt-dropmenu>
            </template>
            

            <template #after-search="scope">
                <div class="btn-group">
                <button @click="refresh()" 
                    class="btn btn-sm btn-outline-secondary"
                >Refresh <i class="fa fa-sync"></i></button>

                <button 
                    v-if="!props.disableActions"
                    @click="newRecord()" 
                    class="btn btn-sm btn-outline-primary"
                >New <i class="fa fa-plus"></i></button>
                </div>
            </template>

            <template #under-header>
                <!-- Bulk Actions Toolbar -->
                <div v-if="selectedRows.length > 0" class="mb-0 p-2 actions-row">
                    <div class="d-flex align-items-center gap-2">
                        <span class="fw-bold text-primary">{{ selectedRows.length }} selected</span>
                        <button 
                            class="btn btn-sm btn-outline-danger"
                            @click="showDeleteModal = true"
                            title="Delete Selected"
                        >
                            <i class="fas fa-trash"></i> Delete
                        </button>
                        <button 
                            class="btn btn-sm btn-outline-secondary me-auto"
                            @click="clearSelection"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>
                
                <div v-if="hasActiveFilters && !props.hideFilters" class="d-flex flex-wrap gap-2 p-2">
                    <span class="text-muted small me-2">Active filters:</span>
                    
                    <!-- Client filter badges -->
                    <span v-for="clientId in currentFilter.client" 
                          :key="`client-${clientId}`" 
                          class="badge bg-primary d-flex align-items-center">
                        Client: {{ getClientName(clientId) }}
                        <button type="button" 
                                class="btn-close btn-close-white ms-2" 
                                @click="removeClientFilter(clientId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Technician filter badges -->
                    <span v-for="techId in currentFilter.technician" 
                          :key="`tech-${techId}`" 
                          class="badge bg-success d-flex align-items-center">
                        Technician: {{ getTechName(techId) }}
                        <button type="button" 
                                class="btn-close btn-close-white ms-2" 
                                @click="removeTechFilter(techId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Date range filter badge -->
                    <span v-if="currentFilter.startDate || currentFilter.endDate" 
                          class="badge bg-info d-flex align-items-center">
                        Date: {{ currentFilter.startDate }} to {{ currentFilter.endDate }}
                        <button type="button" 
                                class="btn-close btn-close-white ms-2" 
                                @click="removeDateFilter()"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Search term badge -->
                    <span v-if="currentFilter.searchTerm"
                          class="badge bg-secondary d-flex align-items-center">
                        Search: {{ currentFilter.searchTerm }}
                        <button type="button" 
                                class="btn-close btn-close-white ms-2" 
                                @click="setFilter({ searchTerm: '' })"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Clear all filters -->
                    <button v-if="hasActiveFilters" 
                            class="btn btn-sm btn-outline-secondary ms-2" 
                            @click="clearAllFilters()">
                      Clear All
                    </button>
                </div>
            </template>

            
            <template #view_string="scope">
                <span v-if="scope.column.name === 'Contact'">
                    {{ scope.row.Contact?.Company || 'N/A' }}
                </span>
                <span v-else-if="scope.column.name === 'RequestId'">
                    {{ scope.row.Request?.Id || 'N/A' }}
                </span>
                <span v-else-if="scope.column.name === 'TechnicianName'">
                    {{ scope.row.Technician?.TechnicianName || 'N/A' }}
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>
            
            <template #view_number="scope">
                <span v-if="scope.column.name === 'ExpenseAmount'">
                    ${{ (scope.value || 0).toFixed(2) }}
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>
        </kt-datatable>
        
        <!-- Bulk Delete Confirmation Modal -->
        <kt-modal
            :visible="showDeleteModal"
            title="Confirm Bulk Delete"
            @dismiss="showDeleteModal = false"
        >
            <template #body>
                <div class="container-fluid">
                    <p class="text-danger mb-3">
                        <i class="fas fa-exclamation-triangle"></i>
                        Are you sure you want to delete {{ selectedRows.length }} expense(s)? This action cannot be undone.
                    </p>
                </div>
            </template>
            <template #footer>
                <button class="btn btn-secondary" @click="showDeleteModal = false">Cancel</button>
                <button class="btn btn-danger" @click="confirmBulkDelete">Delete</button>
            </template>
        </kt-modal>
    </div>
    
    <router-view name="modal"></router-view>
    
    <!-- Embedded Edit Modal -->
    <expense-edit-embedded 
        v-if="props.embedded && showEditModal" 
        :record-id="editingRecordId"
        :request-id="props.requestId"
        :client-id="props.clientId"
        :tech-id="props.techId"
        :pm-only="props.pmOnly"
        @close="closeEditModal"
    />
    
</template>
    
<script setup lang="ts">

import { ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter, RouterView } from "vue-router";
import Api from "@/services/ApiService";
import { ApiResponse } from "@/services/ApiService";
import { ExpenseModel, ExpenseModelFilter } from "@/models/ExpenseModel";
import Cache from "@/services/Cache";
import KtDatatable from "@/components/UI/KtDatatable.vue";
import { KtDatatableModelProp } from "@/components/UI/KtDataTable";
import ClientMultiCheck from "@/components/Clients/ClientMultiCheck.vue";
import TechMultiCheck from "@/components/Techs/TechMultiCheck.vue";
import KtDateRangeSelect from "@/components/UI/KtDateRangeSelect.vue";
import KtModal from "@/components/UI/KtModal.vue";
import ExpenseFilter from '@/components/Expenses/ExpenseFilter.vue';
import ExpenseEditEmbedded from '@/components/Expenses/ExpenseEditEmbedded.vue';
import { useClientStore } from "@/stores/ClientStore";
import { useTechStore } from "@/stores/TechStore";
import KtDropmenu from "@/components/UI/KtDropmenu.vue";

const route = useRoute();
const router = useRouter();

// Props
const props = defineProps({
    requestId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: [Number, String],
        default: null
    },
    techId: {
        type: [Number, String],
        default: null
    },
    hideFilters: {
        type: Boolean,
        default: false
    },
    embedded: {
        type: Boolean,
        default: false
    },
    pmOnly: {
        type: Boolean,
        default: false
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

// Store access
const ClientStore = useClientStore();
const TechStore = useTechStore();

// Computed store lists
const ClientList = computed(() => Object.values(ClientStore.list));
const TechList = computed(() => Object.values(TechStore.list));

// Data state
const total = ref(0);
const rows = ref<ExpenseModel[]>([]);

// Selection state
const selectedRows = ref<number[]>([]);
const selectAll = ref(false);
const showDeleteModal = ref(false);

// ===== NEW CLEAN FILTER SYSTEM =====

// Filter state interface
interface ExpenseListFilter {
    client: number[];
    technician: number[];
    user: string;
    searchTerm: string;
    startDate: string;
    endDate: string;
    page: number;
    size: number;
    sort: Array<Record<string, 'ASC' | 'DESC'>>;
}

// Current filter state (reactive)
const currentFilter = ref<ExpenseListFilter>({
    client: [],
    technician: [],
    user: '',
    searchTerm: '',
    startDate: '',
    endDate: '',
    page: 0,
    size: 25,
    sort: []
});

// Datatable filter for pagination (derived from currentFilter)
const datatableFilter = computed(() => ({
    page: currentFilter.value.page,
    size: currentFilter.value.size,
    total: total.value,
    search: currentFilter.value.searchTerm,
    sort: currentFilter.value.sort
}));

// Date range picker (derived from currentFilter)
const dateRange = computed({
    get: () => ({
        startDate: currentFilter.value.startDate || null,
        endDate: currentFilter.value.endDate || null
    }),
    set: (value) => {
        setFilter({
            startDate: value.startDate || '',
            endDate: value.endDate || ''
        });
    }
});

const actionDropdown = ref<Record<string, boolean>>({});

// Check if any filters are active
const hasActiveFilters = computed(() => {
    return currentFilter.value.client.length > 0 ||
           currentFilter.value.technician.length > 0 ||
           currentFilter.value.searchTerm ||
           currentFilter.value.startDate !== '' ||
           currentFilter.value.endDate !== '';
});

// Legacy filter for compatibility with existing filter components
const filter = computed(() => ({
    client: currentFilter.value.client.map(c => c.toString()).join(','),
    technician: currentFilter.value.technician.map(t => t.toString()).join(','),
    user: currentFilter.value.user,
    searchTerm: currentFilter.value.searchTerm,
    startDate: currentFilter.value.startDate,
    endDate: currentFilter.value.endDate,
    page: currentFilter.value.page.toString()
}));

// ===== URL & FILTER UTILITIES =====

// Parse sort parameter from URL string to KtDatatable sort array format  
const parseSortFromUrl = (sortParam?: string): Array<Record<string, 'ASC' | 'DESC'>> => {
    if (!sortParam) return [];
    
    // Parse comma-separated sort string like "Id,-ExpenseDate,ExpenseAmount"
    // Where "-" prefix means DESC, no prefix means ASC
    return sortParam.split(',').map(sortField => {
        const trimmed = sortField.trim();
        if (trimmed.startsWith('-')) {
            const column = trimmed.substring(1);
            return { [column]: 'DESC' as const };
        } else {
            return { [trimmed]: 'ASC' as const };
        }
    });
};

// Parse URL params to filter object
const parseUrlToFilter = (): ExpenseListFilter => {
    const routeQuery = route.query;
    
    console.debug('Parsing URL params from route.query:', routeQuery);
    console.debug('Raw page param:', routeQuery.page);
    
    const parsed = {
        client: Array.isArray(routeQuery.client) 
            ? routeQuery.client.filter(c => c !== null).map(c => parseInt(c as string))
            : (routeQuery.client ? [parseInt(routeQuery.client as string)] : []),
        technician: Array.isArray(routeQuery.technician) 
            ? routeQuery.technician.filter(t => t !== null).map(t => parseInt(t as string))
            : (routeQuery.technician ? [parseInt(routeQuery.technician as string)] : []),
        user: (routeQuery.user as string) || '',
        searchTerm: (routeQuery.searchTerm as string) || '',
        startDate: (routeQuery.startDate as string) || '',
        endDate: (routeQuery.endDate as string) || '',
        page: parseInt((routeQuery.page as string) || '0'),
        size: parseInt((routeQuery.size as string) || '25'),
        sort: parseSortFromUrl(routeQuery.sort as string)
    };
    
    console.debug('Parsed filter:', parsed);
    return parsed;
};

// Build URL params from filter object
const buildUrlFromFilter = (filter: ExpenseListFilter): string => {
    const params = new URLSearchParams();
    
    // Add array parameters
    filter.client.forEach(c => c && params.append('client', c.toString()));
    filter.technician.forEach(t => t && params.append('technician', t.toString()));
    
    // Add single parameters
    if (filter.user) params.set('user', filter.user);
    if (filter.searchTerm) params.set('searchTerm', filter.searchTerm);
    if (filter.startDate) params.set('startDate', filter.startDate);
    if (filter.endDate) params.set('endDate', filter.endDate);
    
    // Add sort parameter - convert KtDatatable sort array back to URL string
    if (filter.sort && filter.sort.length > 0) {
        const sortString = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        }).join(',');
        params.set('sort', sortString);
    }
    
    // Always include page parameter (even for page 0/1)
    params.set('page', filter.page.toString());
    if (filter.size !== 25) params.set('size', filter.size.toString());
    
    return params.toString();
};

// Save filter to Cache service for persistence (consistent with other lists)
const saveFilterToStorage = (filter: ExpenseListFilter) => {
    try {
        Cache.set('expense-list-filter', filter);
        console.debug('Filter saved to cache:', filter);
    } catch (error) {
        console.error('Failed to save filter to cache:', error);
    }
};

// Load filter from Cache service as fallback
const loadFilterFromStorage = (): ExpenseListFilter => {
    try {
        const stored = Cache.get('expense-list-filter');
        if (stored) {
            console.debug('Loaded filter from cache:', stored);
            return stored;
        }
    } catch (error) {
        console.error('Failed to load filter from cache:', error);
    }
    
    // Return default filter if nothing in cache
    return {
        client: [],
        technician: [],
        user: '',
        searchTerm: '',
        startDate: '',
        endDate: '',
        page: 0,
        size: 25,
        sort: []
    };
};

// ===== API INTEGRATION =====

// Convert filter to API parameters
const buildApiParams = (filter: ExpenseListFilter) => {
    const params: any = {
        Page: filter.page,
        Size: filter.size,
        IncludeRequest: true,
        IncludeContact: true
    };
    
    // If requestId prop is provided, filter by that request only
    if (props.requestId) {
        params.RequestId = props.requestId;
        // Don't apply other filters when filtering by request
        return params;
    }
    
    // Convert client CustomerNumbers to ContactIds for API
    if (filter.client.length > 0) {
        const contactIds = filter.client.map(customerNumber => {
            const client = ClientList.value.find(c => c.CustomerNumber === customerNumber);
            return client?.Id;
        }).filter(id => id !== undefined);
        
        if (contactIds.length > 0) {
            params.ContactId = contactIds;
        }
    }
    
    // Add technician filter
    if (filter.technician.length > 0) params.TechId = filter.technician;
    
    // Add single filters
    if (filter.user) params.TechId = filter.user;
    if (filter.searchTerm) params.SearchTerm = filter.searchTerm;
    if (filter.startDate) {
        params.StartDate = new Date(filter.startDate + " 00:00:00");
    }
    if (filter.endDate) {
        params.EndDate = new Date(filter.endDate + " 23:59:59");
    }
    
    // Add OrderBy parameter for server-side sorting
    if (filter.sort && filter.sort.length > 0) {
        // Convert sort array to comma-separated OrderBy string
        // Example: [{Id: 'ASC'}] -> "Id", [{Id: 'DESC'}] -> "-Id"
        const orderByParts = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        });
        params.OrderBy = orderByParts.join(',');
    }
    
    return params;
};

// Load data from API using current filter
const loadData = async () => {
    try {
        console.debug('loadData called with currentFilter:', currentFilter.value);
        const params = buildApiParams(currentFilter.value);
        console.debug('API params:', params);
        
        const response: ApiResponse<ExpenseModel[]> = await Api.loadExpenses(params, false);
        console.debug('API response:', response);
        
        rows.value = response.Data || [];
        total.value = response.Meta?.Total || 0;
        
        console.debug(`Loaded ${rows.value.length} expense entries, total: ${total.value}`);
    } catch (error) {
        console.error('Failed to load data:', error);
        rows.value = [];
        total.value = 0;
    }
};

// ===== FILTER UPDATE HANDLERS =====

// Handle datatable pagination/sorting updates
const handleDatatableUpdate = (filterUpdate: any) => {
    console.log('Datatable filter update (pagination/sorting):', filterUpdate);
    
    // If embedded mode (requestId prop), update filter directly without routing
    if (props.requestId) {
        if (filterUpdate.page !== undefined) currentFilter.value.page = filterUpdate.page;
        if (filterUpdate.size !== undefined) currentFilter.value.size = filterUpdate.size;
        if (filterUpdate.sort !== undefined) currentFilter.value.sort = filterUpdate.sort || [];
        if (filterUpdate.search !== undefined) currentFilter.value.searchTerm = filterUpdate.search;
        loadData();
        return;
    }
    
    // Create a temporary filter object for URL building (don't update currentFilter yet)
    const tempFilter = { ...currentFilter.value };
    if (filterUpdate.page !== undefined) tempFilter.page = filterUpdate.page;
    if (filterUpdate.size !== undefined) tempFilter.size = filterUpdate.size;
    if (filterUpdate.search !== undefined) tempFilter.searchTerm = filterUpdate.search;
    if (filterUpdate.sort !== undefined) tempFilter.sort = filterUpdate.sort || [];
    
    // Build URL and navigate directly (let route watcher handle the rest)
    const urlParams = buildUrlFromFilter(tempFilter);
    const newUrl = `/expense/list${urlParams ? '?' + urlParams : ''}`;
    
    console.log('Navigating to:', newUrl);
    router.replace(newUrl);
};

// Initialize filter from URL or localStorage
const initializeFilter = () => {
    console.debug('Initializing filter...');
    
    // If embedded mode (requestId prop), skip URL initialization
    if (props.requestId) {
        console.debug('Embedded mode - using default filter');
        return;
    }
    
    // Check if we have URL params
    const hasUrlParams = Object.keys(route.query).length > 0;
    
    if (hasUrlParams) {
        console.debug('Using URL params');
        currentFilter.value = parseUrlToFilter();
    } else {
        console.debug('No URL params, checking cache');
        const cachedFilter = loadFilterFromStorage();
        currentFilter.value = cachedFilter;
        
        // Update URL to reflect loaded filter
        const urlParams = buildUrlFromFilter(currentFilter.value);
        const newUrl = `/expense/list${urlParams ? '?' + urlParams : ''}`;
        router.replace(newUrl);
    }
    
    console.debug('Initialized filter:', currentFilter.value);
};

// Set filter (partial update)
const setFilter = (updates: Partial<ExpenseListFilter>) => {
    console.debug('setFilter called with updates:', updates);
    
    // If embedded mode (requestId prop), ignore filter updates
    if (props.requestId) {
        console.debug('Embedded mode - ignoring filter updates');
        return;
    }
    
    // Merge updates into current filter
    const newFilter = { ...currentFilter.value, ...updates };
    
    // Reset to page 0 when filters change (except when page itself is being updated)
    if (!('page' in updates)) {
        newFilter.page = 0;
    }
    
    console.debug('New filter after merge:', newFilter);
    
    // Save to cache
    saveFilterToStorage(newFilter);
    
    // Build URL and navigate
    const urlParams = buildUrlFromFilter(newFilter);
    const newUrl = `/expense/list${urlParams ? '?' + urlParams : ''}`;
    
    console.debug('Navigating to:', newUrl);
    router.replace(newUrl);
};

// ===== ROUTE WATCHER =====

// Watch route changes and reload data
watch(() => route.query, (newQuery, oldQuery) => {
    // Skip route watching in embedded mode
    if (props.requestId) return;
    
    console.debug('Route query changed:', { newQuery, oldQuery });
    
    // Parse new filter from URL
    const newFilter = parseUrlToFilter();
    
    // Check if filter actually changed (prevent infinite loops)
    const currentParams = buildUrlFromFilter(currentFilter.value);
    const newParams = buildUrlFromFilter(newFilter);
    
    if (currentParams !== newParams) {
        console.debug('Filter changed, updating and loading data');
        currentFilter.value = newFilter;
        
        // Save to cache
        saveFilterToStorage(newFilter);
        
        // Load data with new filter
        loadData();
    } else {
        console.debug('Filter unchanged, skipping reload');
    }
}, { deep: true });

// ===== HELPER FUNCTIONS =====

// Get client name by CustomerNumber
const getClientName = (customerNumber: number) => {
    const client = ClientList.value.find(c => c.CustomerNumber === customerNumber);
    return client?.Company || `Client #${customerNumber}`;
};

// Get tech name by ID
const getTechName = (techId: number) => {
    const tech = TechList.value.find(t => t.Id === techId);
    return tech?.TechnicianName || `Tech #${techId}`;
};

// Remove single client filter
const removeClientFilter = (customerNumber: number) => {
    setFilter({
        client: currentFilter.value.client.filter(c => c !== customerNumber)
    });
};

// Remove single technician filter
const removeTechFilter = (techId: number) => {
    setFilter({
        technician: currentFilter.value.technician.filter(t => t !== techId)
    });
};

// Remove date filter
const removeDateFilter = () => {
    setFilter({ startDate: '', endDate: '' });
};

const clearAllFilters = () => {
    setFilter({
        client: [],
        technician: [],
        searchTerm: '',
        startDate: '',
        endDate: '',
        sort: []
    });
};

// Remove search term filter
const removeSearchTermFilter = () => {
    setFilter({ searchTerm: '' });
};

// ===== LEGACY FUNCTION REPLACEMENTS =====

// Embedded mode state
const showEditModal = ref(false);
const editingRecordId = ref<string | null>(null);

const editRecord = (id: any) => {
    if (props.embedded) {
        editingRecordId.value = id.toString();
        showEditModal.value = true;
    } else {
        router.push({
            path: "/expense/edit/" + id
        });
    }
};

const newRecord = () => {
    if (props.embedded) {
        editingRecordId.value = 'new';
        showEditModal.value = true;
    } else {
        router.push({
            path: "/expense/new"
        });
    }
};

const closeEditModal = () => {
    showEditModal.value = false;
    editingRecordId.value = null;
    // Refresh the list after closing modal
    refresh();
};

const refresh = () => {
    loadData();
};

const deleteRecord = (id: any) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
        // TODO: Implement delete functionality
        console.log("Delete expense:", id);
    }
};

// ===== LIFECYCLE =====

onMounted(() => {
    console.debug('ExpenseList mounted, initializing...');
    
    // Stores auto-load on first access
    // Initialize filter and load data
    initializeFilter();
    loadData();
});

// ===== DATATABLE MODEL =====

// ===== SELECTION MANAGEMENT =====

const toggleRowSelection = (rowId: number) => {
    const index = selectedRows.value.indexOf(rowId);
    if (index > -1) {
        selectedRows.value.splice(index, 1);
    } else {
        selectedRows.value.push(rowId);
    }
    updateSelectAllState();
};

const toggleSelectAll = () => {
    if (selectAll.value) {
        selectedRows.value = [];
        selectAll.value = false;
    } else {
        selectedRows.value = rows.value.map((row: any) => row.Id);
        selectAll.value = true;
    }
};

const updateSelectAllState = () => {
    if (rows.value.length === 0) {
        selectAll.value = false;
        return;
    }
    const allSelected = rows.value.every((row: any) => selectedRows.value.includes(row.Id));
    selectAll.value = allSelected;
};

const clearSelection = () => {
    selectedRows.value = [];
    selectAll.value = false;
};

// ===== BULK ACTIONS =====

const confirmBulkDelete = async () => {
    if (selectedRows.value.length === 0) return;
    
    console.warn(`Bulk delete for ${selectedRows.value.length} expense(s) - API implementation needed`);
    // TODO: Implement when API is available
    // try {
    //   for (const id of selectedRows.value) {
    //     await Api.deleteExpense(id);
    //   }
    //   Alert.notify({ text: `Successfully deleted ${selectedRows.value.length} expense(s)`, type: 'success' });
    //   clearSelection();
    //   await refresh();
    // } catch (error) {
    //   console.error('Error deleting expenses:', error);
    //   Alert.notify({ text: 'Failed to delete some expenses', type: 'error' });
    // }
    
    showDeleteModal.value = false;
};

// KtDatatable model definition for Expense list
const expenseModel: Array<KtDatatableModelProp> = [
    {
        name: "Select",
        label: "Select",
        type: "select",
        width: 60,
        locked: true,
        draggable: false
    },
    {
        name: "Actions",
        label: "Actions",
        type: "actions",
        display: true
    },
    {
        name: "Id",
        label: "ID",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "Contact",
        label: "Client",
        display: true,
        type: "string"
    },
    {
        name: "RequestId",
        label: "Request ID",
        display: true,
        type: "string"
    },
    {
        name: "ExpenseDate",
        label: "Date",
        display: true,
        type: "date",
        sortable: true
    },
    {
        name: "ExpenseAmount",
        label: "Amount",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "ExpenseNotes",
        label: "Notes",
        display: true,
        type: "string"
    },
    {
        name: "TechnicianName",
        label: "Technician",
        display: true,
        type: "string"
    },
    {
        name: "CreateDate",
        label: "Created",
        display: true,
        type: "date",
        sortable: true
    },
    {
        name: "ReimbursementCheckNum",
        label: "Check #",
        display: true,
        type: "number"
    }
];
    
</script>
